/**
 * Product import (Settings → Export/Import), ATL-2067.
 *
 * The import used to call `updateProduct` with a FULL product built from the file row: every
 * store's price and stock from one column, `activeInStore = true` everywhere, and every field the
 * file does not carry reset (`ingredients = []`, `productImages = []`, `isComposite = false`, …) —
 * a composite product lost its tech card. Stock was written straight into `storeStock`, past the
 * books. Matching by the file's ID column never ran (`newValues.id` was never set), and `parseInt`
 * cut 2.5 to 2 and the leading zero off a barcode.
 *
 * Now, for an EXISTING product:
 *   - only the columns that are present AND mapped are written, and price and stock only in the
 *     store chosen for the import; `activeInStore` is never touched;
 *   - a stock change is a revision — `saveNewRevision`, the writer the Revisions screen uses — so
 *     it carries a `type: 'revision'` movement linked to a revision with its author;
 *   - the file's ID column matches first, then the barcode.
 * A NEW product gets the row's price in every store (it is on sale everywhere, as when created by
 * hand, and an active product without a price would sell at 0) but the row's stock ONLY in the
 * store chosen for the import: 0 everywhere else (ATL-2070; it used to get the row's stock in every
 * store). The stock reaches the books through the product create itself (`saveNewProduct` writes a
 * `newProduct` movement per store from `storeStock`), exactly as the product screen's create does,
 * which also writes 0 for the stores it was not given a count for. Numbers keep their fraction and
 * the barcode is kept as the text in the file.
 *
 * Order (`runProductImport`): the whole file is planned and validated before anything is written;
 * a plan with errors writes nothing. Then the ONE revision is saved first, so its all-or-nothing
 * refusal (ATL-1793: a counted product missing in Firestore) happens before any product changed.
 * Then new categories/suppliers, then the field updates, then the new products. A failure there
 * stops the run and the report names the rows that were written and the rows that were not.
 */

import { cellToString } from "./importCells";
import { parseDecimalAmount } from "./decimalInput";

/** The import's mappable columns, keyed as `ExportImportScreen` keys `columnMappingByName`. */
export const IMPORT_COLUMNS = [
  "id",
  "name",
  "category",
  "barcode",
  "price",
  "purchasePrice",
  "inStock",
  "supplier",
];

/** A column counts only when it is mapped to a real column of the file (the «ignore» option is index -1). */
export const mappedColumnIndex = (mapping, column) => {
  const index = mapping?.[column]?.index;
  return Number.isInteger(index) && index >= 0 ? index : null;
};

const cellText = (row, index) =>
  index === null ? "" : cellToString(row?.[index]);

/**
 * A number from a cell: '' → null (no value); unreadable → NaN. Both separators, spaces as
 * thousands separators ('1 500,5'), the fraction kept.
 */
const readNumber = (row, index) => {
  if (index === null) return null;
  const cell = row?.[index];
  if (typeof cell === "number") return Number.isFinite(cell) ? cell : NaN;
  const text = cellToString(cell).replace(/\s/g, "");
  if (text === "") return null;
  const parsed = parseDecimalAmount(text);
  return parsed === null ? NaN : parsed;
};

/**
 * Plan the import of `rows` (the sheet as read, row 0 = header). Pure: no I/O.
 *
 * Row numbers in the result are the FILE's row numbers (the header is row 1), so a merchant can
 * find them in the spreadsheet.
 *
 * @returns {{
 *   errors: Array<{row: number, code: string, column?: string, value?: string, firstRow?: number}>,
 *   skippedExisting: number[],
 *   updates: Array<{row: number, product: object, fields: object, categoryKey?: string, supplierKey?: string}>,
 *   creates: Array<{row: number, fields: object, categoryKey: string|null, supplierKey: string|null}>,
 *   revisionItems: Array<{row: number, id: string, name: string, inStock: number, inStockOld: number, individualAmount: number}>,
 * }}
 */
export const planProductImport = ({
  rows,
  mapping,
  store,
  storesDict,
  productsDict,
  productsByBarcodeDict,
  updateExisting,
  biggestBarcode,
}) => {
  if (store === null || store === undefined)
    throw new Error("planProductImport: a store is required");
  // The file's stock is put on this store's key; a store that is not one of the organization's
  // would put it on a key no screen shows and leave every real store at 0.
  if (!Object.prototype.hasOwnProperty.call(storesDict ?? {}, store))
    throw new Error(
      `planProductImport: store ${store} is not one of the organization's stores`,
    );

  const col = Object.fromEntries(
    IMPORT_COLUMNS.map((column) => [
      column,
      mappedColumnIndex(mapping, column),
    ]),
  );
  const plan = {
    errors: [],
    skippedExisting: [],
    updates: [],
    creates: [],
    revisionItems: [],
  };
  const rowOfProduct = new Map();
  const rowOfNewBarcode = new Map();
  let nextBarcode = biggestBarcode;

  for (let index = 1; index < rows.length; index++) {
    const row = rows[index];
    const fileRow = index + 1;
    const errorCountBefore = plan.errors.length;

    const numbers = {};
    for (const column of ["price", "purchasePrice", "inStock"]) {
      const value = readNumber(row, col[column]);
      if (Number.isNaN(value)) {
        plan.errors.push({
          row: fileRow,
          code: "invalidNumber",
          column,
          value: cellText(row, col[column]),
        });
      }
      numbers[column] = value;
    }
    if (numbers.price !== null && numbers.price < 0) {
      plan.errors.push({
        row: fileRow,
        code: "invalidNumber",
        column: "price",
        value: cellText(row, col.price),
      });
    }
    if (numbers.purchasePrice !== null && numbers.purchasePrice < 0) {
      plan.errors.push({
        row: fileRow,
        code: "invalidNumber",
        column: "purchasePrice",
        value: cellText(row, col.purchasePrice),
      });
    }

    const id = cellText(row, col.id);
    const barcode = cellText(row, col.barcode);
    const name = cellText(row, col.name);

    // ATL-2067 criterion 4: the file's ID first, then the barcode.
    let existing = null;
    if (id !== "" && productsDict[id]) existing = productsDict[id];
    if (!existing && barcode !== "" && productsByBarcodeDict[barcode])
      existing = productsByBarcodeDict[barcode];
    if (!existing && id !== "") {
      // An ID the file names but this organization does not have is not a new product: creating
      // one would duplicate a product the merchant believes they are editing.
      plan.errors.push({ row: fileRow, code: "idNotFound", value: id });
      continue;
    }

    if (existing) {
      if (rowOfProduct.has(existing.id)) {
        plan.errors.push({
          row: fileRow,
          code: "duplicateProduct",
          firstRow: rowOfProduct.get(existing.id),
        });
        continue;
      }
      rowOfProduct.set(existing.id, fileRow);
      if (plan.errors.length > errorCountBefore) continue;
      if (!updateExisting) {
        plan.skippedExisting.push(fileRow);
        continue;
      }

      // Criterion 1: only mapped columns with a value. An empty cell asserts nothing.
      const fields = {};
      if (name !== "") fields.name = name;
      if (barcode !== "" && String(existing.barcode ?? "") !== barcode)
        fields.barcode = barcode;
      if (numbers.purchasePrice !== null)
        fields.purchasePrice = numbers.purchasePrice;
      // Criterion 2: the chosen store only. updateProduct writes the delta against the read model
      // (ATL-1041), so only `salePrices.<store>` reaches Firestore.
      if (numbers.price !== null)
        fields.salePrices = {
          ...(existing.salePrices || {}),
          [store]: numbers.price,
        };

      const update = { row: fileRow, product: existing, fields };
      const categoryKey =
        col.category === null ? "" : cellText(row, col.category);
      if (categoryKey !== "") update.categoryKey = String(row[col.category]);
      const supplierKey =
        col.supplier === null ? "" : cellText(row, col.supplier);
      if (supplierKey !== "") update.supplierKey = String(row[col.supplier]);

      // Criterion 3: stock is never a field write; a change becomes a revision item.
      if (numbers.inStock !== null) {
        const inStockOld = existing.storeStock?.[store] || 0;
        if (numbers.inStock !== inStockOld) {
          plan.revisionItems.push({
            row: fileRow,
            name: existing.name,
            id: existing.id,
            inStock: numbers.inStock,
            inStockOld,
            // The Revisions screen's formula (RevisionDetailScreen calculateIndividualAmount).
            individualAmount:
              (existing.purchasePrice || 0) * (numbers.inStock - inStockOld),
          });
        }
      }
      if (
        Object.keys(fields).length > 0 ||
        update.categoryKey !== undefined ||
        update.supplierKey !== undefined
      ) {
        plan.updates.push(update);
      }
      continue;
    }

    // A new product.
    if (barcode !== "") {
      if (rowOfNewBarcode.has(barcode)) {
        plan.errors.push({
          row: fileRow,
          code: "duplicateBarcode",
          value: barcode,
          firstRow: rowOfNewBarcode.get(barcode),
        });
        continue;
      }
      rowOfNewBarcode.set(barcode, fileRow);
    }
    if (plan.errors.length > errorCountBefore) continue;

    let newBarcode = barcode;
    if (newBarcode === "") {
      nextBarcode += 1;
      newBarcode = nextBarcode;
    }
    const salePrices = {};
    const storeStock = {};
    const activeInStore = {};
    for (const key in storesDict) {
      salePrices[key] = numbers.price ?? 0;
      // ATL-2070: the file counts the chosen store only.
      storeStock[key] = key === store ? (numbers.inStock ?? 0) : 0;
      activeInStore[key] = true;
    }
    plan.creates.push({
      row: fileRow,
      fields: {
        name: col.name === null ? undefined : row[col.name],
        purchasePrice: numbers.purchasePrice ?? 0,
        storeStock,
        salePrices,
        barcode: newBarcode,
        activeInStore,
      },
      categoryKey:
        col.category === null ? null : String(row[col.category] ?? ""),
      supplierKey:
        col.supplier === null ? null : String(row[col.supplier] ?? ""),
    });
  }

  return plan;
};

/**
 * The revision the import's stock changes become — one per import (one store), the same shape the
 * Revisions screen hands `saveNewRevision` (RevisionDetailScreen `save`).
 */
export const buildImportRevision = (
  revisionItems,
  { store, cashierUID, organizationId, date, dailyStatID },
) => {
  const items = revisionItems.map(
    ({ name, id, inStock, inStockOld, individualAmount }) => ({
      name,
      id,
      inStock,
      inStockOld,
      individualAmount,
    }),
  );
  let amount = 0;
  let negativeAmount = 0;
  let positiveAmount = 0;
  for (const item of items) {
    amount += item.individualAmount;
    if (item.individualAmount < 0) negativeAmount += item.individualAmount;
    else positiveAmount += item.individualAmount;
  }
  return {
    cashierUID,
    productCategory: null,
    date,
    dailyStatID,
    items,
    amount,
    negativeAmount,
    positiveAmount,
    // No per-item override exists in an import, so the corrected total is the computed one —
    // what calculateCorrectedAmount gives on the Revisions screen with every override box empty.
    correctedAmount: amount,
    store,
    organizationId,
  };
};

/** The fields every new product gets, exactly as the import always wrote them. */
const NEW_PRODUCT_DEFAULTS = {
  packagePrice: 0,
  type: false,
  isComposite: false,
  manufacturedBeforeSale: false,
  ingredients: [],
  productImages: [],
  stockThreshold: 1,
  importance: 1,
  punchCardEnabled: false,
  isHiddenInNewSale: false,
  description: "",
  activeInEcommerce: false,
  defaultQuantity: 1,
  productTags: [],
};

const OTHER = { id: "other" };

/**
 * Execute a plan. Writes go through the injected writers only.
 *
 * @param plan the result of planProductImport; a plan with errors is refused here too
 * @param deps {
 *   saveRevision(info) → ref,             // saveNewRevision(db, info)
 *   updateProduct(product, payload),      // updateProduct(db, product, payload, productsDict)
 *   createProduct(payload),               // saveNewProduct(db, payload)
 *   createCategory(name) → {id},          // only called for categories mapped to «new»
 *   createSupplier(name) → {id},
 *   categoryMapping, supplierMapping,     // file value → existing {id} or {id: 'new'}
 *   meta: {store, uid, organizationId, lastChangeStore, now, productDailyStatID, revisionDailyStatID, productUnit},
 *   onProgress?(done),
 * }
 * @returns {{
 *   status: 'invalid' | 'revisionFailed' | 'failed' | 'done',
 *   revisionId?: string, updatedRows: number[], createdRows: number[],
 *   failedRow?: number, notAttemptedRows: number[], error?: unknown,
 * }}
 */
export const runProductImport = async (plan, deps) => {
  const { meta } = deps;
  const report = { updatedRows: [], createdRows: [], notAttemptedRows: [] };
  const pendingRows = () => [
    ...plan.updates.map((u) => u.row),
    ...plan.creates.map((c) => c.row),
  ];

  if (plan.errors.length > 0) {
    return { ...report, status: "invalid", notAttemptedRows: pendingRows() };
  }

  // 1. The revision first: if Firestore refuses it, nothing else has been written.
  if (plan.revisionItems.length > 0) {
    const info = buildImportRevision(plan.revisionItems, {
      store: meta.store,
      cashierUID: meta.uid,
      organizationId: meta.organizationId,
      date: meta.now,
      dailyStatID: meta.revisionDailyStatID,
    });
    try {
      const ref = await deps.saveRevision(info);
      report.revisionId = ref?.id;
    } catch (error) {
      return {
        ...report,
        status: "revisionFailed",
        error,
        notAttemptedRows: pendingRows(),
      };
    }
  }

  const productMeta = {
    dailyStatID: meta.productDailyStatID,
    lastChangeBy: meta.uid,
    lastChangeAt: meta.now,
    lastChangeSource: "import",
    lastChangeStore: meta.lastChangeStore,
    organizationId: meta.organizationId,
  };

  const steps = [
    ...plan.updates.map((update) => ({
      kind: "update",
      row: update.row,
      item: update,
    })),
    ...plan.creates.map((create) => ({
      kind: "create",
      row: create.row,
      item: create,
    })),
  ];
  // One lookup per distinct file value, so a "new" category or supplier is created once. Two
  // local Maps, each used only through get/has/set, so the W-writer census can prove that
  // `.set` is a Map and not a Firestore write (a Map picked by key cannot be proven).
  const fromMapping = async (mapped, create) => {
    if (!mapped) return OTHER;
    if (mapped.id === "new") return create();
    return mapped;
  };
  const categoryCache = new Map();
  const supplierCache = new Map();
  const resolveCategory = async (key) => {
    if (key === null || key === undefined) return OTHER;
    if (categoryCache.has(key)) return categoryCache.get(key);
    const value = await fromMapping(deps.categoryMapping?.[key], () =>
      deps.createCategory(key),
    );
    categoryCache.set(key, value);
    return value;
  };
  const resolveSupplier = async (key) => {
    if (key === null || key === undefined) return OTHER;
    if (supplierCache.has(key)) return supplierCache.get(key);
    const value = await fromMapping(deps.supplierMapping?.[key], () =>
      deps.createSupplier(key),
    );
    supplierCache.set(key, value);
    return value;
  };

  for (let index = 0; index < steps.length; index++) {
    const step = steps[index];
    try {
      if (step.kind === "update") {
        const payload = { ...step.item.fields, ...productMeta };
        if (step.item.categoryKey !== undefined)
          payload.categoryID = (
            await resolveCategory(step.item.categoryKey)
          )?.id;
        if (step.item.supplierKey !== undefined)
          payload.supplierID = (
            await resolveSupplier(step.item.supplierKey)
          )?.id;
        await deps.updateProduct(step.item.product, payload);
        report.updatedRows.push(step.row);
      } else {
        const category = await resolveCategory(step.item.categoryKey);
        const supplier = await resolveSupplier(step.item.supplierKey);
        await deps.createProduct({
          ...NEW_PRODUCT_DEFAULTS,
          ...step.item.fields,
          categoryID: category?.id,
          supplierID: supplier?.id,
          productUnit: meta.productUnit,
          ...productMeta,
        });
        report.createdRows.push(step.row);
      }
    } catch (error) {
      return {
        ...report,
        status: "failed",
        failedRow: step.row,
        error,
        notAttemptedRows: steps.slice(index + 1).map((s) => s.row),
      };
    }
    deps.onProgress?.(index + 1);
  }

  return { ...report, status: "done" };
};
